import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TableContainer,
  Typography,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import cx from 'classnames';
import { IMedispanStagingDiff } from 'models/application-manager/medispan/IMedispanStagingDiff';
import { medispanStagingStyles } from './index.styles';

type SortableField = 'gpi' | 'gpi10' | 'drugName';
type SortDirection = 'asc' | 'desc';

interface IMedispanStagingTableProps extends WithStyles<typeof medispanStagingStyles> {
  rows: IMedispanStagingDiff[];
}

const MedispanStagingTable = (props: IMedispanStagingTableProps): JSX.Element => {
  const { classes, rows } = props;

  const [sortField, setSortField] = React.useState<SortableField>('gpi');
  const [sortDir, setSortDir] = React.useState<SortDirection>('asc');

  const handleSort = (field: SortableField) => {
    if (field === sortField) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = (a.current ?? a.staged)?.[sortField]?.toString() ?? '';
      const bVal = (b.current ?? b.staged)?.[sortField]?.toString() ?? '';
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortField, sortDir]);

  const sortLabel = (field: SortableField, label: string) => (
    <TableSortLabel active={sortField === field} direction={sortDir} onClick={() => handleSort(field)}>
      {label}
    </TableSortLabel>
  );

  if (rows.length === 0) {
    return (
      <Typography className={classes.emptyState}>No staging differences found.</Typography>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell} rowSpan={2}>Drug Descriptor ID</TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>{sortLabel('gpi', 'GPI')}</TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>{sortLabel('gpi10', 'GPI 10')}</TableCell>
            <TableCell className={classes.tableHeadCell} colSpan={2}>{sortLabel('drugName', 'Drug Name')}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCellRight}>Staged</TableCell>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCellRight}>Staged</TableCell>
            <TableCell className={classes.subHeaderCell}>Current</TableCell>
            <TableCell className={classes.subHeaderCell}>Staged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, idx) => {
            const renderPair = (
              currentVal: string | null | undefined,
              stagedVal: string | null | undefined,
            ) => {
              const cur = currentVal ?? '—';
              const stg = stagedVal ?? '—';
              const isDifferent = cur !== stg;
              return (
                <>
                  <TableCell className={classes.tableCell}>{cur}</TableCell>
                  <TableCell className={cx(classes.tableCell, { [classes.changedValue]: isDifferent })}>
                    {stg}
                  </TableCell>
                </>
              );
            };

            return (
              <TableRow key={`${row.drugDescriptorId}`} className={cx(classes.parentRow, idx % 2 === 0 ? classes.evenParent : classes.oddParent)}>
                <TableCell className={classes.tableCell}>{row.drugDescriptorId}</TableCell>
                {renderPair(row.current?.gpi, row.staged?.gpi)}
                {renderPair(row.current?.gpi10, row.staged?.gpi10)}
                {renderPair(row.current?.drugName, row.staged?.drugName)}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const MedispanStagingTableStyled = withStyles(medispanStagingStyles)(MedispanStagingTable);
